import { sortBy } from 'lodash';
import React, { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ORG_CHART_HR_MAPPING } from 'routes/HumanResourcesModule/business/definitions';
import { StyHrDataGrid } from 'routes/HumanResourcesModule/components/HrDataGrid/HrDataGrid';
import { HrSimulation } from 'routes/HumanResourcesModule/Simulation/helpers/types';
import { slugify } from 'helpers/formatHelper';
import EditEnvelopeMantyStatusHypothesisCellRenderer from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/Envelope/MantyStatusHypothesis/EditEnvelopeMantyStatusHypothesisCellRenderer';
import CreateEnvelopeMantyStatusHypothesisCellRenderer from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/Envelope/MantyStatusHypothesis/CreateEnvelopeMantyStatusHypothesisCellRenderer';
import {
    DEFAULT_GRID_PROPS,
    getDefaultGridProps,
} from 'routes/HumanResourcesModule/components/HrDataGrid/helpers/gridHelpers';
import { useGetEnvelopeMantyStatusHypotheses } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/Envelope/MantyStatusHypothesis/helpers/hooks/mantyStatusHypothesis';
import { useQueryClient } from '@tanstack/react-query';
import { ApplyChangesButton } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/components/ApplyChangesButton';
import { HR_SIMULATION_NON_RECURRENT_ENVELOPE_DATA_API_KEY } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/Agents/helpers/hooks/agentsSimulation';
import { useEnvelopeMantyStatusHypothesesChangesContext } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/Envelope/MantyStatusHypothesis/contexts/EnvelopeMantyStatusHypothesesChangesContext';
import {
    TRACKABLE_EVENTS,
    TRACKABLE_EVENTS_METADATA,
    trackEventPRH,
} from 'helpers/trackerHelpers';
import { useGetHrSimulationNonRecurrentEnvelopeData } from './helpers/hooks/nonRecurrentEnvelope';
import { getColDefs } from './helpers/colDefinitions';
import AddEnvelopeMantyStatusHypothesisButton from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/Envelope/MantyStatusHypothesis/components/Modals/AddEnvelopeMantyStatusHypothesisButton';
import { useGetNonRecurrentHypothesisTypePerMantyStatus } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/Agents/components/HypothesisTypePerMantyStatus/helpers/hooks/nonRecurrentHypothesisTypePerMantyStatus';

import { MantyStatus } from 'routes/HumanResourcesModule/Settings/HrPositionMapping/helpers/constants';
import { snakify } from 'helpers/syntaxHelper';
import { NonRecurrentDataGridContext } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/Agents/components/NonRecurrentEnvelope/helpers/types';

type Props = {
    hrSimulation: HrSimulation;
};

const NonRecurrentEnvelopeGrid = ({
    hrSimulation,
}: Props): React.ReactElement => {
    const { t } = useTranslation();
    const { id: simulationId, title } = hrSimulation;
    const { hasChanges, setHasChanges } =
        useEnvelopeMantyStatusHypothesesChangesContext();
    const queryClient = useQueryClient();

    const {
        data: envelopeMantyStatusHypothesesResponse,
        isLoading: areHypothesesLoading,
        isFetching: areHypothesesFetching,
    } = useGetEnvelopeMantyStatusHypotheses(simulationId);

    const envelopeMantyStatusHypotheses =
        envelopeMantyStatusHypothesesResponse ?? [];

    const {
        data: hrSimulationAgentsDataResponse,
        isLoading: isHrSimulationAgentsResponseLoading,
        isFetching: isHrSimulationAgentsResponseFetching,
    } = useGetHrSimulationNonRecurrentEnvelopeData(simulationId);
    const {
        data: hypothesisTypePerMantyStatus,
        isLoading: isHypothesisTypePerMantyStatusLoading,
    } = useGetNonRecurrentHypothesisTypePerMantyStatus(hrSimulation.id);
    const nonRecurrentMantyStatuses = useMemo(
        () =>
            (hypothesisTypePerMantyStatus &&
                Object.keys(hypothesisTypePerMantyStatus).filter(
                    (key) => hypothesisTypePerMantyStatus[key] === 'envelope',
                )) ||
            [],
        [hypothesisTypePerMantyStatus],
    );

    const nonRecurrentEnvelopeData =
        sortBy(hrSimulationAgentsDataResponse?.data, [ORG_CHART_HR_MAPPING]) ||
        [];

    const columnDefs = getColDefs({
        hrSimulation,
        envelopeMantyStatusHypotheses,
    });

    const gridProps = {
        ...getDefaultGridProps(),
        components: {
            ...DEFAULT_GRID_PROPS.components,
            createEnvelopeMantyStatusHypothesisCellRenderer:
                CreateEnvelopeMantyStatusHypothesisCellRenderer,
            editEnvelopeMantyStatusHypothesisCellRenderer:
                EditEnvelopeMantyStatusHypothesisCellRenderer,
        },
        context: {
            envelopeMantyStatusHypotheses,
        } satisfies NonRecurrentDataGridContext,
    };

    const exportFileName = t(
        'humanResources.simulation.envelopeGrid.dataFileName',
        {
            title: slugify(title),
        },
    );

    const reloadNonRecurrentEnvelopeData = () => {
        queryClient.invalidateQueries({
            queryKey: [
                HR_SIMULATION_NON_RECURRENT_ENVELOPE_DATA_API_KEY,
                simulationId,
            ],
        });
        setHasChanges(false);
        trackEventPRH(
            TRACKABLE_EVENTS.human_resources.simulation.nonRecurrent
                .envelopeDataRefresh,
        );
    };

    const headerActions = (
        <ApplyChangesButton
            isLoading={isHrSimulationAgentsResponseFetching}
            isDisabled={hasChanges === false}
            onClick={reloadNonRecurrentEnvelopeData}
            title={t('humanResources.simulation.actualizeTable')}
        />
    );
    const filteredMantyStatuses = useMemo(
        () =>
            Object.values(MantyStatus).filter((status) =>
                nonRecurrentMantyStatuses.some(
                    (key) => snakify(key) === status,
                ),
            ) as MantyStatus[],
        [nonRecurrentMantyStatuses],
    );

    const footerActions = (
        <AddEnvelopeMantyStatusHypothesisButton
            isLoading={
                isHrSimulationAgentsResponseFetching ||
                isHypothesisTypePerMantyStatusLoading
            }
            hrSimulation={hrSimulation}
            mantyStatusHypotheses={envelopeMantyStatusHypotheses}
            nonRecurrentMantyStatuses={filteredMantyStatuses}
        />
    );

    const isLoading =
        isHrSimulationAgentsResponseLoading || areHypothesesLoading;
    const isFetching =
        isHrSimulationAgentsResponseFetching || areHypothesesFetching;

    return (
        <StyHrDataGrid
            title={t('humanResources.simulation.envelopeGrid.title')}
            gridProps={gridProps}
            trackEventMetadata={{
                gridName:
                    TRACKABLE_EVENTS_METADATA.human_resources.fullScreen
                        .nonRecurrentEnvelopeGrid,
            }}
            rowData={nonRecurrentEnvelopeData}
            columnDefs={columnDefs}
            exportFileName={exportFileName}
            isLoading={isLoading}
            isFetching={isFetching}
            cardHeaderActions={headerActions}
            cardFooterActions={footerActions}
        />
    );
};

export default memo(NonRecurrentEnvelopeGrid);
